import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftRight, Plus, Check, Play } from "lucide-react";
import { inr } from "@/lib/format";

/* Shared eyebrow + heading, matching the storefront rhythm. */
function Head({ eyebrow, title, link, linkLabel = "View all", light = false }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <p className={`eyebrow ${light ? "!text-gold" : "!text-gold"}`}>{eyebrow}</p>
        <h2 className={`font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1.5 ${light ? "text-ivory" : "text-ink"}`}>{title}</h2>
      </div>
      {link && <Link to={link} className="text-sm text-brand underline underline-offset-4 decoration-gold hover:text-brand-2 transition-colors shrink-0">{linkLabel}</Link>}
    </div>
  );
}

/* ---------------- 1. Shoppable Videos ----------------
   Muted autoplay loops with a poster fallback; each frame pins one product. */
export function ShoppableVideos({ data }) {
  if (!data?.items?.length) return null;
  return (
    <section className="container-x mt-16" data-testid="shoppable-videos">
      <Head eyebrow="WATCH & SHOP" title={data.title || "See it in motion"} link="/shop" />
      <div className="flex gap-5 overflow-x-auto pb-3 mt-6 snap-x scrollbar-none" style={{ scrollbarWidth: "none" }}>
        {data.items.map((it, i) => (
          <ShoppableVideoCard key={i} item={it} index={i} />
        ))}
      </div>
    </section>
  );
}

function ShoppableVideoCard({ item, index }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const p = item.product;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.play?.().then(() => setPlaying(true)).catch(() => {}); }
      else { el.pause?.(); }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const to = p ? `/products/${p.slug}` : (item.link || "/shop");
  return (
    <div className="snap-start shrink-0 w-[248px] sm:w-[280px]" data-testid={`shoppable-video-${index}`}>
      <Link to={to} className="block relative rounded-2xl overflow-hidden bg-brand border border-brand/10 aspect-[9/14] group">
        {item.video ? (
          <video ref={ref} src={item.video} poster={item.poster || undefined}
            muted loop playsInline preload="metadata"
            className="absolute inset-0 h-full w-full object-cover" />
        ) : item.poster ? (
          <img src={item.poster} alt={item.title || ""} className="absolute inset-0 h-full w-full object-cover" />
        ) : <div className="absolute inset-0 bg-brand-light" />}
        {!playing && item.video && (
          <span className="absolute top-3 left-3 h-8 w-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white"><Play size={14} fill="currentColor" /></span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/75 via-black/25 to-transparent">
          {item.title && <p className="text-white font-heading text-lg font-semibold leading-tight">{item.title}</p>}
          {item.subtitle && <p className="text-white/70 text-xs mt-0.5">{item.subtitle}</p>}
          {p && (
            <div className="mt-3 flex items-center gap-2 bg-white/95 rounded-xl p-2" data-testid={`shoppable-product-${index}`}>
              <div className="h-11 w-11 rounded-lg overflow-hidden bg-ivory shrink-0">
                {(p.images || [])[0]
                  ? <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover" />
                  : <div className="h-full w-full bg-brand-light" />}
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-ink truncate">{p.title}</p>
                <p className="text-[13px] gold-num font-semibold text-brand">{inr(p.price_min)}</p>
              </div>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

/* ---------------- 2. Lookbook with hotspots ----------------
   A campaign image with dots bound to products; tap a dot to reveal the piece. */
export function LookbookHotspots({ data }) {
  const [active, setActive] = useState(null);
  if (!data || (!data.image && !data.video_url)) return null;
  const spots = data.hotspots || [];
  return (
    <section className="container-x mt-16" data-testid="lookbook-hotspots">
      <Head eyebrow={data.eyebrow || "THE LOOKBOOK"} title={data.title || "Shop the look"} />
      <div className="relative mt-6 rounded-2xl overflow-hidden border border-brand/10 bg-brand-light">
        {data.video_url ? (
          <video src={data.video_url} poster={data.poster || undefined} autoPlay muted loop playsInline className="w-full h-auto max-h-[80vh] object-cover" />
        ) : (
          <img src={data.image} alt={data.title || "Lookbook"} className="w-full h-auto max-h-[80vh] object-cover" data-testid="lookbook-image" />
        )}
        {spots.map((h, i) => (
          <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${h.x}%`, top: `${h.y}%` }}>
            <button
              onClick={() => setActive(active === i ? null : i)}
              className={`h-6 w-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center transition-transform ${active === i ? "bg-gold scale-110" : "bg-brand animate-pulse"}`}
              aria-label={`View ${h.product?.title || "product"}`} data-testid={`lookbook-dot-${i}`}>
              <Plus size={13} className="text-white" />
            </button>
            {active === i && h.product && (
              <Link to={`/products/${h.product.slug}`}
                className="absolute z-10 top-8 left-1/2 -translate-x-1/2 w-52 bg-white rounded-xl shadow-2xl border border-brand/10 p-2.5 flex items-center gap-3"
                data-testid={`lookbook-card-${i}`}>
                <div className="h-14 w-14 rounded-lg overflow-hidden bg-ivory shrink-0">
                  {h.product.image ? <img src={h.product.image} alt={h.product.title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-brand-light" />}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-ink line-clamp-2">{h.product.title}</p>
                  <p className="text-[13px] gold-num font-semibold text-brand mt-0.5">{inr(h.product.price_min)}</p>
                </div>
              </Link>
            )}
          </div>
        ))}
        {data.credit && <p className="absolute bottom-2 right-3 text-[10px] font-mono text-white/70">{data.credit}</p>}
      </div>
    </section>
  );
}

/* ---------------- 3. Bundle builder (curated set) ----------------
   Every Siyaara piece is made to order and configured on its own page (purity,
   colour, size), so a bundle links each piece to its product page — never a
   one-click cart write that would skip configuration. Prices are "from". */
export function BundleBuilder({ data }) {
  const navigate = useNavigate();
  const [picked, setPicked] = useState([]);
  if (!data?.products?.length) return null;
  const offer = data.offer || {};
  const buyN = Number(offer.buy_n || 0);
  const offerLabel = offer.type === "pct_off" ? `${offer.pct}% off`
    : offer.type === "fixed_total" ? `a set price of ${inr(offer.fixed_total)}`
    : offer.type === "bogo" ? `buy ${offer.buy_n} get ${offer.get_m} free`
    : "";
  const toggle = (id) => setPicked((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const chosen = data.products.filter((p) => picked.includes(p.id));
  const subtotal = chosen.reduce((s, p) => s + (p.price_min || 0), 0);
  const goConfigure = () => { if (chosen[0]) navigate(`/products/${chosen[0].slug}`); };
  return (
    <section className="container-x mt-16" data-testid="bundle-builder">
      <Head eyebrow="BUILD A SET" title={data.title || "Build your bundle"} />
      {data.subtitle && <p className="text-ink-2 text-sm mt-2 max-w-xl">{data.subtitle}</p>}
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {data.products.map((p) => {
            const on = picked.includes(p.id);
            return (
              <button key={p.id} onClick={() => toggle(p.id)}
                className={`text-left rounded-xl border bg-white overflow-hidden transition-all btn-press ${on ? "border-gold ring-2 ring-gold/40" : "border-brand/10 hover:border-brand/30"}`}
                data-testid={`bundle-pick-${p.slug}`}>
                <div className="relative aspect-square bg-ivory">
                  {(p.images || [])[0] ? <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-brand-light" />}
                  <span className={`absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center ${on ? "bg-gold text-brand" : "bg-white/90 text-brand border border-brand/15"}`}>
                    {on ? <Check size={14} /> : <Plus size={14} />}
                  </span>
                </div>
                <div className="p-2.5">
                  <p className="text-[13px] font-medium text-ink line-clamp-1">{p.title}</p>
                  <p className="text-[13px] gold-num font-semibold text-brand mt-0.5">from {inr(p.price_min)}</p>
                </div>
              </button>
            );
          })}
        </div>
        <aside className="bg-white border border-brand/10 rounded-2xl p-5 h-fit lg:sticky lg:top-24" data-testid="bundle-tray">
          <p className="font-heading text-lg font-semibold text-ink">Your set</p>
          {buyN > 0 && (
            <div className="mt-3">
              <div className="h-1.5 rounded-full bg-brand-light overflow-hidden">
                <div className="h-full bg-gold transition-all" style={{ width: `${Math.min(100, (chosen.length / buyN) * 100)}%` }} />
              </div>
              {offerLabel && (
                <p className="text-xs text-ink-2 mt-2" data-testid="bundle-tier-hint">
                  {chosen.length >= buyN
                    ? `Your set qualifies for ${offerLabel} — applied on eligible items at checkout.`
                    : `Add ${buyN} or more and unlock ${offerLabel} at checkout.`}
                </p>
              )}
            </div>
          )}
          <div className="mt-4 space-y-2 max-h-52 overflow-y-auto">
            {chosen.length === 0 && <p className="text-sm text-ink-2">Tap pieces to add them to your set.</p>}
            {chosen.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} className="flex items-center justify-between text-sm group" data-testid={`bundle-tray-item-${p.slug}`}>
                <span className="truncate text-ink group-hover:text-brand pr-2">{p.title}</span>
                <span className="gold-num text-ink-2 shrink-0">from {inr(p.price_min)}</span>
              </Link>
            ))}
          </div>
          {chosen.length > 0 && (
            <div className="mt-4 border-t border-brand/10 pt-3 flex justify-between font-semibold text-ink text-base">
              <span>Set total</span><span className="gold-num">from {inr(subtotal)}</span>
            </div>
          )}
          <button onClick={goConfigure} disabled={!chosen.length}
            className="mt-4 w-full h-11 rounded-full bg-brand text-ivory hover:bg-brand-2 transition-colors btn-press disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="bundle-add-btn">
            Configure &amp; add your pieces
          </button>
          <p className="text-[11px] text-ink-2 mt-2 text-center">Each piece is made to order — choose purity, colour &amp; size on its page.</p>
        </aside>
      </div>
    </section>
  );
}

/* ---------------- 4. Image compare ----------------
   A draggable before/after slider — Siyaara's wax-to-gold craft story. */
export function ImageCompare({ data }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  if (!data?.before || !data?.after) return null;
  const move = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
  };
  return (
    <section className="container-x mt-16" data-testid="image-compare">
      <Head eyebrow={data.eyebrow || "FROM WAX TO GOLD"} title={data.title || "Made to order, made for you"} />
      {data.caption && <p className="text-ink-2 text-sm mt-2 max-w-xl">{data.caption}</p>}
      <div
        ref={ref}
        className="relative mt-6 rounded-2xl overflow-hidden border border-brand/10 bg-brand-light select-none aspect-[16/10] max-w-4xl cursor-ew-resize"
        onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
        onMouseMove={(e) => dragging.current && move(e.clientX)}
        onMouseUp={() => (dragging.current = false)}
        onMouseLeave={() => (dragging.current = false)}
        onTouchStart={(e) => move(e.touches[0].clientX)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
        data-testid="image-compare-track">
        <img src={data.after} alt={data.after_label || "After"} className="absolute inset-0 h-full w-full object-cover pointer-events-none" draggable={false} />
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
          <img src={data.before} alt={data.before_label || "Before"} className="absolute inset-0 h-full w-full object-cover" style={{ width: ref.current?.offsetWidth || "100%" }} draggable={false} />
        </div>
        {data.before_label && <span className="absolute top-3 left-3 bg-black/60 text-white text-[11px] font-mono tracking-widest px-2.5 py-1 rounded-full pointer-events-none">{data.before_label}</span>}
        {data.after_label && <span className="absolute top-3 right-3 bg-black/60 text-white text-[11px] font-mono tracking-widest px-2.5 py-1 rounded-full pointer-events-none">{data.after_label}</span>}
        <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -translate-x-1/2 w-0.5 bg-white/90" />
          <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center text-brand" data-testid="image-compare-handle">
            <ArrowLeftRight size={17} />
          </div>
        </div>
      </div>
    </section>
  );
}
